import React from 'react';
import PropTypes from 'prop-types';

import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { withViewport } from '../../util/contextHelpers';

import {
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  Page,
  ExternalLink,
  NamedLink,
  JoinPreFooter,
} from '../../components';

import hartwallLogo from '../../assets/hartwall-logo.svg';
import stepArrowIcon from '../../assets/hartwall-step-arrow.svg';
import checkmarkIcon from '../../assets/hartwall-checkmark-outline.svg';
import mismeImage from '../../assets/misme_halonen-quote.jpeg';

import { TopbarContainer } from '..';

import css from './SuccessStoryHartwallPage.css';

const MAX_MOBILE_SCREEN_WIDTH = 962;

const boldChunk = chunks => <strong style={{ fontWeight: 600 }}>{chunks}</strong>;

const QuoteBlock = ({ quoteId }) => (
  <div className={css.quoteBlock}>
    <p className={css.quoteText}>
      <FormattedMessage id={quoteId} />
    </p>
    <div className={css.quotePerson}>
      <img src={mismeImage} alt="Misme Halonen" className={css.avatar} loading="lazy" />
      <div className={css.quotePersonInfo}>
        <p className={css.personName}>
          <FormattedMessage id="SuccessStoryHartwall.contactName" />
        </p>
        <p className={css.personTitle}>
          <FormattedMessage id="SuccessStoryHartwall.contactTitle" />
        </p>
      </div>
    </div>
  </div>
);

export const SuccessStoryHartwallPageComponent = props => {
  const { intl, scrollingDisabled, viewport } = props;

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const schemaTitle = intl.formatMessage({ id: 'SuccessStoryHartwall.schemaTitle' });
  const schemaDescription = intl.formatMessage({
    id: 'SuccessStoryHartwall.schemaDescription',
  });

  const handleShowSpaceHome = () => {
    if (typeof window === 'object') {
      window.Intercom('showSpace', 'home');
    }
  };

  const isMobileLayout = viewport.width <= MAX_MOBILE_SCREEN_WIDTH;

  const customerCard = (
    <div className={css.customerCard}>
      <div className={css.customerCardSection}>
        <p className={css.cardLabel}>
          <FormattedMessage id="SuccessStoryHartwall.customerLabel" />
        </p>
        <div className={css.customerLogoWrapper}>
          <img src={hartwallLogo} alt="Hartwall" className={css.customerLogo} />
        </div>
        <p className={css.customerDescription}>
          <FormattedMessage id="SuccessStoryHartwall.customerDescription" />
        </p>
      </div>
      <div className={css.customerCardSection}>
        <p className={css.cardLabel}>
          <FormattedMessage id="SuccessStoryHartwall.contactLabel" />
        </p>
        <div className={css.contactRow}>
          <img src={mismeImage} alt="Misme Halonen" className={css.avatar} loading="lazy" />
          <div className={css.contactInfo}>
            <p className={css.contactName}>
              <FormattedMessage id="SuccessStoryHartwall.contactName" />
            </p>
            <p className={css.personTitle}>
              <FormattedMessage id="SuccessStoryHartwall.contactTitle" />
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const consultationCard = (
    <div className={css.consultationCard}>
      <div className={css.consultationCardContent}>
        <p className={css.consultationTitle}>
          <FormattedMessage id="SuccessStoryHartwall.consultationTitle" />
        </p>
        <p className={css.consultationText}>
          <FormattedMessage id="SuccessStoryHartwall.consultationText" />
        </p>
      </div>
      <ExternalLink className={css.consultationButton} onClick={handleShowSpaceHome}>
        <FormattedMessage id="SuccessStoryHartwall.consultationButton" />
      </ExternalLink>
    </div>
  );

  const stepNumbers = ['01', '02', '03', '04'];

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="SuccessStoryHartwallPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.heroSection}>
            <div className={css.heroContent}>
              <div className={css.heroBreadcrumbs}>
                <NamedLink name="SuccessStoriesPage" className={css.heroBreadcrumbLink}>
                  <FormattedMessage id="SuccessStoryHartwall.heroBreadcrumbs1" />
                </NamedLink>
                <span className={css.heroBreadcrumbSeparator}>
                  <FormattedMessage id="SuccessStoryHartwall.heroBreadcrumbs2" />
                </span>
                <span className={css.heroBreadcrumbCurrent}>
                  <FormattedMessage id="SuccessStoryHartwall.heroBreadcrumbs3" />
                </span>
              </div>
              <h1 className={css.heroTitle}>
                <FormattedMessage id="SuccessStoryHartwall.heroTitle" />
              </h1>
              <p className={css.heroSubtitle}>
                <FormattedMessage id="SuccessStoryHartwall.heroSubtitle" />
              </p>
            </div>
          </div>
          <div className={css.contentWrapper}>
            {isMobileLayout ? (
              customerCard
            ) : (
              <div className={css.sidebar}>
                {customerCard}
                {consultationCard}
              </div>
            )}
            <div className={css.article}>
              <section className={css.textSection}>
                <p className={css.sectionEyebrow}>
                  <FormattedMessage id="SuccessStoryHartwall.section1Eyebrow" />
                </p>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="SuccessStoryHartwall.section1Title" />
                </h2>
                <p className={css.sectionText}>
                  <FormattedMessage id="SuccessStoryHartwall.section1Paragraph1" />
                </p>
                <p className={css.sectionText}>
                  <FormattedMessage id="SuccessStoryHartwall.section1Paragraph2" />
                </p>
                <p className={css.sectionText}>
                  <FormattedMessage
                    id="SuccessStoryHartwall.section1Paragraph3"
                    values={{ b: boldChunk }}
                  />
                </p>
              </section>
              <section className={css.textSection}>
                <p className={css.sectionEyebrow}>
                  <FormattedMessage id="SuccessStoryHartwall.section2Eyebrow" />
                </p>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="SuccessStoryHartwall.section2Title" />
                </h2>
                <p className={css.sectionText}>
                  <FormattedMessage id="SuccessStoryHartwall.section2Paragraph1" />
                </p>
                <QuoteBlock quoteId="SuccessStoryHartwall.quote1Text" />
              </section>
              <section className={css.textSection}>
                <p className={css.sectionEyebrow}>
                  <FormattedMessage id="SuccessStoryHartwall.section3Eyebrow" />
                </p>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="SuccessStoryHartwall.section3Title" />
                </h2>
                <p className={css.sectionText}>
                  <FormattedMessage id="SuccessStoryHartwall.section3Paragraph1" />
                </p>
                <div className={css.stepsRow}>
                  {stepNumbers.map((stepNumber, index) => (
                    <React.Fragment key={stepNumber}>
                      <div className={css.stepCard}>
                        <p className={css.stepNumber}>{stepNumber}</p>
                        <div className={css.stepCardContent}>
                          <p className={css.stepTitle}>
                            <FormattedMessage id={`SuccessStoryHartwall.step${index + 1}Title`} />
                          </p>
                          <p className={css.stepText}>
                            <FormattedMessage id={`SuccessStoryHartwall.step${index + 1}Text`} />
                          </p>
                        </div>
                      </div>
                      {index < stepNumbers.length - 1 ? (
                        <img src={stepArrowIcon} alt="" className={css.stepArrow} />
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
                <div className={css.checkPill}>
                  <img src={checkmarkIcon} alt="" className={css.checkPillIcon} />
                  <p className={css.checkPillText}>
                    <FormattedMessage id="SuccessStoryHartwall.stepsPillText" />
                  </p>
                </div>
                <p className={css.sectionText}>
                  <FormattedMessage id="SuccessStoryHartwall.section3Paragraph2" />
                </p>
              </section>
              <section className={css.textSection}>
                <p className={css.sectionEyebrow}>
                  <FormattedMessage id="SuccessStoryHartwall.section4Eyebrow" />
                </p>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="SuccessStoryHartwall.section4Title" />
                </h2>
                <p className={css.sectionText}>
                  <FormattedMessage
                    id="SuccessStoryHartwall.section4Paragraph1"
                    values={{ b: boldChunk }}
                  />
                </p>
                <p className={css.sectionText}>
                  <FormattedMessage id="SuccessStoryHartwall.section4Paragraph2" />
                </p>
                <QuoteBlock quoteId="SuccessStoryHartwall.quote2Text" />
                <p className={css.sectionText}>
                  <FormattedMessage id="SuccessStoryHartwall.section4Paragraph3" />
                </p>
                <p className={css.sectionText}>
                  <FormattedMessage id="SuccessStoryHartwall.section4Paragraph4" />
                </p>
              </section>
              <NamedLink name="SuccessStoriesPage" className={css.moreStoriesButton}>
                <FormattedMessage id="SuccessStoryHartwall.moreStoriesButton" />
              </NamedLink>
            </div>
            {isMobileLayout ? consultationCard : null}
          </div>
          <JoinPreFooter showContactButton />
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

SuccessStoryHartwallPageComponent.defaultProps = {
  scrollingDisabled: false,
};

const { number, shape } = PropTypes;

SuccessStoryHartwallPageComponent.propTypes = {
  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SuccessStoryHartwallPage = compose(
  withRouter,
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(SuccessStoryHartwallPageComponent);

export default SuccessStoryHartwallPage;
